import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TUser } from '@shared/interfaces/TUser';
import { computeMomentumLong, computeMomentumShort } from '@shared/utils/computeMomentum';
import { YearMonth } from 'src/app/interfaces/year-month.interface';

import { TProject, TRegionBrick } from '@shared/interfaces/TProject';
import { momentumLongDesc, momentumShortDesc } from 'src/app/constants/momentum-description.constant';
import { KamLeaderboardDataset } from 'src/app/interfaces/kam-leaderboard-dataset.interface';
import { KamLeaderboardItem, KamLeaderboardTableData } from 'src/app/interfaces/kam-leaderboard-table-data.interface';
import { Region } from 'src/app/interfaces/region.interface';
import { AuthService } from 'src/app/services/auth.service';
import { CategoriesService } from 'src/app/services/categories.service';
import { KamReportsService } from './../../services/kam-reports.service';

@Component({
  selector: 'app-kam-leaderboard',
  templateUrl: './kam-leaderboard.component.html',
  styleUrls: ['./kam-leaderboard.component.scss']
})
export class KamLeaderboardComponent implements OnInit, AfterViewInit {

  @Input() project: TProject;
  kamLeaderboardData: KamLeaderboardTableData;
  // private subscriptions: Subscription[] = [];

  kamLeaderboardDisplayedColumnsHead: string[] = [
    'name',
    'total',
    'delta4Month',
    'deltaPY',
    'totalSalesInEuro',
    'utilisationPharmascope', // total # patients
  ];
  kamLeaderboardDisplayedColumnsTail: string[] = [
    // 'budget',
    'patientPotential',
    'patientUtilisation',
    'percentage',
    'accountProfiles',
    // 'lastUpdate'
  ];
  kamLeaderboardDisplayedColumns: string[] = [];

  competitors: { id: string, key: string, name: string, patients: number }[] = [];

  kamLeaderboardDataSource: MatTableDataSource<KamLeaderboardItem>;
  @ViewChild(MatSort) kamLeaderboardSort: MatSort;

  public momentumLongDesc: string = momentumLongDesc;
  public momentumShortDesc: string = momentumShortDesc;

  constructor(
    private _kamReportsService: KamReportsService,
    private _authService: AuthService,
    private _categoriesService: CategoriesService,
  ) {}

  ngOnInit(): void {
    const kamLeaderboardData: KamLeaderboardTableData = this.buildKamLeaderboardTableDataset(this.project);
    this.kamLeaderboardDisplayedColumns = [
      ...this.kamLeaderboardDisplayedColumnsHead,
      ...this.competitors.map(competitor => competitor.key),
      ...this.kamLeaderboardDisplayedColumnsTail,
    ];
    this.kamLeaderboardDataSource = new MatTableDataSource([kamLeaderboardData.nationalAverage, ...kamLeaderboardData.kams]);
  }

  ngAfterViewInit(): void {
    this.kamLeaderboardDataSource.sort = this.kamLeaderboardSort;
    this.kamLeaderboardDataSource.sortingDataAccessor = (item, property) => {
      const value = item[property];
      // If value is a number, return it; if not, return a value that sorts last
      return typeof value === 'number' ? value : Number.NEGATIVE_INFINITY;
    };
  }

  buildKamLeaderboardTableDataset(project: TProject): KamLeaderboardTableData {
    const user = this._authService.getCurrentUser() as TUser;
    const closestSalesDate: YearMonth = this._kamReportsService.getClosestSalesDate(this.project?.accountBricks);
    const regions: Region[] = this._kamReportsService.getRegions(user, this.project);
    const filteredBricks: TRegionBrick[] = this._kamReportsService.filterBricksBySelectedRegions(project.accountBricks, regions);
    this.competitors = project.competitors.map((competitor, index) => ({ id: competitor.id, key: `competitor_${index}`, name: competitor.name, patients: 0 }));

    // split bricks by regions
    const kamLeaderboardDataset: KamLeaderboardDataset = {};
    regions.forEach(region => {
      const regionBricks = filteredBricks.filter(brick => brick.regionId === region.id);
      // console.log('KAM LEADERBOARD Region bricks', regionBricks);
      const territoryTotal = this._kamReportsService.getTotalFromClosestMonth(regionBricks, 'sales');
      const territoryTotalSalesInEuro = this._kamReportsService.getTotalFromClosestMonth(regionBricks, 'salesInEuro');
      const territoryBudget = this._kamReportsService.sumAllPlannedBudgets(project.accountRegions, closestSalesDate.year, [region]);
      const momentumData = this._kamReportsService.getLastXMonthsTotal(regionBricks, 16, 'sales');
      const momentumShort = Math.round(computeMomentumShort(momentumData));
      const momentumLong = Math.round(computeMomentumLong(momentumData));
      const centers = this._kamReportsService.allCenters;
      const regionCenters = centers.filter(center => {
        const projectsData = center.projectsData.filter(pd => pd.projectId === project.id && pd.c_project_account_region === region.id);
        if (projectsData.length) {
          return true;
        }
        return false;
      });
      this.competitors.forEach(competitor => {
        competitor.patients = this._kamReportsService.getCompetitorsTotalFromClosestMonth(competitor.id, regionBricks, 'patients');
      });

      let patientPotentialKam = 0;
      let patientUtilisationKam = 0;
      let percentOfPatientPotentialKam = 0;
      let utilisation = 0;

      regionCenters.forEach(center => {
        center.projectsData.forEach(pd => {
          // console.log('PD', pd, project.id, region.id);
          if (pd.projectId === project.id && pd.c_project_account_region === region.id) {
            patientPotentialKam = patientPotentialKam + +pd.c_proj_patient_potential1;
            patientUtilisationKam = patientUtilisationKam + +pd.patient_utilisation1;
            percentOfPatientPotentialKam = (patientUtilisationKam / patientPotentialKam) * 100;
          }
        });
      });
      utilisation = this._kamReportsService.getTotalFromClosestMonth(regionBricks, 'patients');
      kamLeaderboardDataset[region.id] = {
        region: region,
        bricks: regionBricks,
        data: {
          name: region.name,
          total: territoryTotal,
          delta4Month: momentumShort,
          deltaPY: momentumLong,
          totalSalesInEuro: territoryTotalSalesInEuro,
          utilisationPharmascope: utilisation,
          budget: territoryBudget,
          patientPotential: patientPotentialKam,
          patientUtilisation: patientUtilisationKam,
          percentage: Math.round(percentOfPatientPotentialKam),
          accountProfiles: regionCenters.length,
          // lastUpdate: new Date('2000-01-01'),
        },
      };
      this.competitors.forEach(competitor => {
        kamLeaderboardDataset[region.id].data[competitor.key] = competitor.patients;
      });
    });
    const result: KamLeaderboardTableData  = this.buildKamLeaderboardTableData(kamLeaderboardDataset);
    return result;
  }

  buildKamLeaderboardTableData(dataset: KamLeaderboardDataset): KamLeaderboardTableData {
    const regions: string[] = Object.keys(dataset);
    const noRegions: number = regions.length;

    // build average
    const average: KamLeaderboardItem = {
      name: 'National average',
      total: 0,
      totalSalesInEuro: 0,
      delta4Month: 0,
      deltaPY: 0,
      budget: 0,
      patientPotential: 0,
      patientUtilisation: 0,
      percentage: 0,
      utilisationPharmascope: 0,
      accountProfiles: 0,
      // lastUpdate: new Date('2000-01-01'),
    };
    this.competitors.forEach(competitor => {
      average[competitor.key] = 0;
    });

    regions.forEach(regionId => {
      average.total = average.total + this.getNumber(dataset[regionId].data.total);
      average.totalSalesInEuro = average.totalSalesInEuro + this.getNumber(dataset[regionId].data.totalSalesInEuro);
      average.delta4Month = average.delta4Month + this.getNumber(dataset[regionId].data.delta4Month);
      average.deltaPY = average.deltaPY + this.getNumber(dataset[regionId].data.deltaPY);
      average.budget = average.budget + this.getNumber(dataset[regionId].data.budget);
      average.patientPotential = average.patientPotential + this.getNumber(dataset[regionId].data.patientPotential);
      average.patientUtilisation = average.patientUtilisation + this.getNumber(dataset[regionId].data.patientUtilisation);
      average.percentage = average.percentage + this.getNumber(dataset[regionId].data.percentage);
      average.utilisationPharmascope = average.utilisationPharmascope + this.getNumber(dataset[regionId].data.utilisationPharmascope);
      average.accountProfiles = average.accountProfiles + this.getNumber(dataset[regionId].data.accountProfiles);
      this.competitors.forEach(competitor => {
        average[competitor.key] = +average[competitor.key] + this.getNumber(dataset[regionId].data[competitor.key]);
      });
    });

    average.total = Math.round(average.total / noRegions);
    average.totalSalesInEuro = Math.round(average.totalSalesInEuro / noRegions);
    average.delta4Month = Math.round(average.delta4Month / noRegions);
    average.deltaPY = Math.round(average.deltaPY / noRegions);
    average.budget = Math.round(average.budget / noRegions);
    average.patientPotential = Math.round(average.patientPotential / noRegions);
    average.patientUtilisation = Math.round(average.patientUtilisation / noRegions);
    average.percentage = Math.round(average.percentage / noRegions);
    average.utilisationPharmascope = Math.round(average.utilisationPharmascope / noRegions);
    average.accountProfiles = Math.round(average.accountProfiles / noRegions);
    this.competitors.forEach(competitor => {
      average[competitor.key] = Math.round(+average[competitor.key] / noRegions);
    });



    const result: KamLeaderboardTableData = {
      nationalAverage: average,
      kams: [],
    };

    regions.forEach(regionId => {
      const region: KamLeaderboardItem = {
        name: dataset[regionId].region.name,
        total: dataset[regionId].data.total,
        totalSalesInEuro: dataset[regionId].data.totalSalesInEuro,
        delta4Month: dataset[regionId].data.delta4Month,
        deltaPY: dataset[regionId].data.deltaPY,
        budget: dataset[regionId].data.budget,
        patientPotential: dataset[regionId].data.patientPotential,
        patientUtilisation: dataset[regionId].data.patientUtilisation,
        percentage: dataset[regionId].data.percentage,
        utilisationPharmascope: dataset[regionId].data.utilisationPharmascope,
        accountProfiles: dataset[regionId].data.accountProfiles,
        // lastUpdate: dataset[regionId].data.lastUpdate,
      };
      this.competitors.forEach(competitor => {
        // region[competitor.key] = competitor.patients;
        region[competitor.key] = dataset[regionId].data[competitor.key] || 0;
      });
      result.kams.push(region);
    });
    return result;
  }

  getNumber(val: any): number {
    return isNaN(+val) ? 0 : +val;
  }

  printData(item: any): string {
    return isNaN(+item) ? 'N/A' : new Intl.NumberFormat().format(Math.round(item)).toString();
  }

  public getHeader(field: string): string {
    return this._categoriesService.getFieldDescription(field, this.project.id);
  }
}
